import {useMemo} from "react";
import chroma from "chroma-js";
import {TableOverlay} from "../types";
import {useReadStore} from "../hooks/useReadStore";
import type {
    FloorplanReservationItemDTO
} from "crqlar-react-common/dist/models/floorplan/floorplanReservationItem.dto";
import {getReservationDisplay} from "../utils/reservation-utils";

interface Props {
    tableOverlays: TableOverlay[];
    visibleReservationIds: Set<string>;
}

interface ReservationLineProps {
    reservationItem: FloorplanReservationItemDTO;
    overlayById: Map<string, TableOverlay>;
}

interface LineData {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    color: string;
}

const ReservationConnectionLines: React.FC<ReservationLineProps> = ({
                                                                        reservationItem,
                                                                        overlayById,
                                                                    }) => {
    const {borderColor} = getReservationDisplay({
        reservation: reservationItem.restaurantReservation,
        profiles: reservationItem.profiles,
    });

    const lines = useMemo(() => {
        const result: LineData[] = [];
        const tables = reservationItem.tableResources ?? [];

        if (tables.length < 2) return result;

        const color = chroma(borderColor).alpha(0.5).css();

        for (let i = 0; i < tables.length - 1; i++) {
            const fromOverlay = tables[i].id ? overlayById.get(tables[i].id!) : undefined;
            const toOverlay = tables[i + 1].id ? overlayById.get(tables[i + 1].id!) : undefined;
            if (!fromOverlay || !toOverlay) continue;

            result.push({
                x1: fromOverlay.x + fromOverlay.width / 2,
                y1: fromOverlay.y + fromOverlay.height / 2,
                x2: toOverlay.x + toOverlay.width / 2,
                y2: toOverlay.y + toOverlay.height / 2,
                color,
            });
        }

        return result;
    }, [reservationItem, overlayById, borderColor]);

    if (!lines.length) return null;

    return (
        <>
            {lines.map((line, index) => (
                <line
                    key={`${reservationItem.restaurantReservation?.id ?? "reservation"}-${index}`}
                    x1={line.x1}
                    y1={line.y1}
                    x2={line.x2}
                    y2={line.y2}
                    stroke={line.color}
                    strokeWidth={3}
                    strokeLinecap="round"
                />
            ))}
        </>
    );
};

const ConnectionLines: React.FC<Props> = ({
                                              tableOverlays,
                                              visibleReservationIds
                                          }) => {
    const assignedReservations = useReadStore(s => s.assignedReservations);

    const overlayById = useMemo(() => {
        return new Map(tableOverlays.map(overlay => [overlay.id, overlay]));
    }, [tableOverlays]);

    const visibleMultiTable = useMemo(() => {
        return assignedReservations.filter(
            (r) => r.reservationId && visibleReservationIds.has(r.reservationId) && (r.tableResources?.length ?? 0) >= 2,
        );
    }, [assignedReservations, visibleReservationIds]);

    if (!visibleMultiTable.length) return null;

    return (
        <svg style={{
            position: 'absolute',
            top: 0,
            left: 0,
            overflow: 'visible',
            pointerEvents: 'none'
        }}>
            {visibleMultiTable.map((reservationItem, index) => (
                <ReservationConnectionLines
                    key={reservationItem.restaurantReservation?.id ?? `reservation-${index}`}
                    reservationItem={reservationItem}
                    overlayById={overlayById}
                />
            ))}
        </svg>
    );
};

export default ConnectionLines;
